'use client';

import PageContent from '@/components/organisms/de-uni/page-content/page-content';
import { defaultSearchParams } from '@/constants/default-search-params';
import { i18n } from '@/i18n-config';
import IBrandLandingPageViewModel from '@/models/viewModels/IBrandLandingPageViewModel';
import {
  IFacet,
  useSearch,
  getSearchQuery,
  ISidebarNavigation,
  LoadingSpinner,
  NavLinksEntity,
  SidebarNavigation,
  SearchHookProps,
} from '@into-discovery/browsewebshared';
import { usePathname } from '@/navigation';
import { useMemo, useCallback } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import colorStyles from '@/styles/utils/variables.module.scss';
import { AxiosResponse } from 'axios';

type Props = {
  pageContent: IBrandLandingPageViewModel;
  mapsApiKey: string;
};
export default function DirectEntryUniversityProvider({ pageContent, mapsApiKey }: Props) {
  const pathname = usePathname();

  const searchBrandId = pageContent.brand.id;

  const studyProgramsParams = useMemo(() => {
    const directEntryFacet: IFacet = {
      id: `entryRoutes`,
      title: `Entry Routes`,
      values: [`Direct Entry`],
    };

    const brandFacet: IFacet = {
      id: `brandId`,
      title: `Brand Id`,
      values: [searchBrandId],
    };
    const defaultFacets: IFacet[] = [directEntryFacet, brandFacet];

    const result: SearchHookProps & { searchQuery?: string } = {
      ...defaultSearchParams,
      facets: defaultFacets,
    };

    result.searchQuery = getSearchQuery(result);

    return result;
  }, [searchBrandId]);

  const browseContentProcessing = useCallback(
    async (data: AxiosResponse<any, any>) => {
      const results = data?.data || [];

      const content = { ...pageContent, studyPrograms: results };

      return content;
    },
    [pageContent],
  );

  const {
    data: browseContent,
    isLoading,
    error,
  } = useSearch(
    studyProgramsParams.searchQuery,
    defaultSearchParams.searchApiSettings,
    pageContent,
    browseContentProcessing,
    [studyProgramsParams.searchQuery],
  );

  // Construct Sidebar Nav Props
  // It is different as we are pulling brand data here via GQL
  const sidebarNavProps: ISidebarNavigation = {
    topImage: {
      fields: {
        image: [],
        altText: ``,
        size: ``,
      },
    },
    links: [],
  };

  // Sidebar Top Image
  sidebarNavProps.topImage.fields.image =
    pageContent?.brand?.brandSidebarNavigation?.topImage?.image;
  sidebarNavProps.topImage.fields.altText =
    pageContent?.brand?.brandSidebarNavigation?.topImage?.altText;
  sidebarNavProps.topImage.fields.size = pageContent?.brand?.brandSidebarNavigation?.topImage?.size;

  // Sidebar Links
  pageContent?.brand?.brandSidebarNavigation?.linksCollection?.links?.forEach((link) => {
    if (link) {
      const fields: NavLinksEntity = {
        fields: {
          urlLink: ``,
          displayName: ``,
          application: ``,
        },
      };
      fields.fields.urlLink = link.urlLink;
      fields.fields.displayName = link.displayName;
      fields.fields.application = link.application;

      sidebarNavProps.links.push(fields);
    }
  });

  return (
    <>
      {!!error && <p>Error loading data</p>}

      {isLoading && (
        <div>
          <LoadingSpinner src="/next-assets/loaders/into.svg" />
        </div>
      )}

      <style>{`
          :root {
            --brand-color: ${pageContent.brand.primaryColour};
          }
        `}</style>
      <Container>
        {pageContent?.brand?.brandSidebarNavigation ? (
          <div className="custom-page-template">
            <Row>
              <Col className="col-lg-3 px-0 px-lg-3">
                <SidebarNavigation
                  sidebarNavigationProps={sidebarNavProps}
                  sidebarBrandColor={colorStyles.primary}
                  b2cFlag={true}
                  pagePath={pathname}
                  locales={i18n.locales}
                />
              </Col>
              <Col className="col-lg-9 py-lg-4 py-0">
                {browseContent && (
                  <PageContent
                    content={browseContent}
                    searchBrandId={searchBrandId}
                    mapsApiKey={mapsApiKey}
                  />
                )}
              </Col>
            </Row>
          </div>
        ) : (
          <div className="custom-page-template">
            <Row>
              <Col className="col-lg-9">
                {browseContent && (
                  <PageContent
                    content={browseContent}
                    searchBrandId={searchBrandId}
                    mapsApiKey={mapsApiKey}
                  />
                )}
              </Col>
            </Row>
          </div>
        )}
        <div className="visually-hidden">v0.1</div>
      </Container>
    </>
  );
}
